import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Building2, Users } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { Link } from 'react-router';
import { Card } from '../../../components/ui/Card';
import { PageContainer, PageHeader } from '../../../components/ui/Page';
import { Skeleton } from '../../../components/ui/Skeleton';
import { useAuth, useCan } from '../../auth/AuthProvider';
import { teamApi, teamKeys } from '../../team/api';
import { tenantApi, tenantKeys } from '../api';

// ApexCharts is large: load it only when the dashboard shows charts, outside the initial bundle.
const LeadCharts = lazy(() => import('../components/LeadCharts').then((m) => ({ default: m.LeadCharts })));

function Stat({
  label,
  value,
  icon: Icon,
  to,
}: {
  label: string;
  value: string;
  icon: typeof Users;
  to?: string;
}) {
  const body = (
    <Card className="flex items-center gap-4 p-5">
      <span
        aria-hidden
        className="flex size-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200"
      >
        <Icon className="size-5" />
      </span>
      <div>
        <p className="text-2xl font-semibold tabular-nums text-fg">{value}</p>
        <p className="text-sm text-fg-muted">{label}</p>
      </div>
    </Card>
  );
  return to ? (
    <Link to={to} className="rounded-lg focus-visible:outline-2">
      {body}
    </Link>
  ) : (
    body
  );
}

/** Dashboard: account health plus the lead overview charts (docs/design/15 §3.2, ADR-045). */
export function DashboardPage() {
  const { me } = useAuth();
  const can = useCan();
  const staff = useQuery({
    queryKey: teamKeys.staff({ pageSize: 1 }),
    queryFn: () => teamApi.staff({ pageSize: 1 }),
    enabled: can('staff.view'),
  });
  const branches = useQuery({
    queryKey: teamKeys.branches,
    queryFn: () => teamApi.branches(),
    enabled: can('branches.view'),
  });
  const entitlements = useQuery({ queryKey: tenantKeys.entitlements, queryFn: tenantApi.entitlements });

  const limit = (key: string) => {
    const value = entitlements.data?.[key]?.limit;
    return value === null || value === undefined ? '' : ` / ${value}`;
  };

  return (
    <PageContainer>
      <PageHeader
        title={`Welcome, ${me?.user.fullName.split(' ')[0] ?? ''}`}
        description={me?.branding?.displayName ?? me?.tenant?.name}
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {can('staff.view') ? (
          <Stat
            label="Staff members"
            icon={Users}
            to="/app/team/staff"
            value={staff.data ? `${staff.data.meta.total ?? 0}${limit('limit.staff')}` : '…'}
          />
        ) : null}
        {can('branches.view') ? (
          <Stat
            label="Branches"
            icon={Building2}
            to="/app/team/branches"
            value={
              branches.data
                ? `${branches.data.data.filter((b) => b.status === 'active').length}${limit('limit.branches')}`
                : '…'
            }
          />
        ) : null}
      </div>
      {can('leads.view') ? (
        <div className="mt-6">
          <Suspense fallback={<Skeleton className="h-72" />}>
            <LeadCharts />
          </Suspense>
        </div>
      ) : null}
      {can('tenant.configure') ? (
        <Link
          to="/app/settings/business"
          className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline dark:text-brand-300"
        >
          Review business settings <ArrowRight aria-hidden className="size-4" />
        </Link>
      ) : null}
    </PageContainer>
  );
}
